import { useMemo, useState } from 'react';

import { GAP_TYPE_META, GAP_TYPE_ORDER } from '../gap-meta';
import { useGaps } from '../state/gaps';
import type { Gap, GapType } from '../types';
import { GEO_ALL } from '../types';

import { GapCard } from './GapCard';
import { NewFormRequiredDialog } from './NewFormRequiredDialog';

/**
 * Stream-level board — one column per gap type.
 *
 * Behavior decided after the spec was written: the board surfaces every
 * gap logged anywhere on the stream, including form-scoped gaps. This
 * gives the LPO a single read of "what's wrong with this stream" rather
 * than forcing them to drill into each form to find a workflow gap.
 *
 * (Spec §3.3 originally described a stream board limited to
 * `new_form_required`; that wording is stale — the implementation is the
 * SoT. Also see `docs/specs/hybrid-app-spec.md` §3.3 update.)
 *
 * The top-bar geography filter applies as a row filter.
 */
export function StreamGapsBoard({
  stream,
  topBarGeo,
}: {
  stream: string;
  topBarGeo: string;
}) {
  const filters = useMemo(
    () => ({
      stream,
      geography: topBarGeo === GEO_ALL ? undefined : topBarGeo,
    }),
    [stream, topBarGeo],
  );
  const { gaps, loading, error, refresh } = useGaps(filters);
  const [dialogOpen, setDialogOpen] = useState(false);

  const grouped = useMemo(() => groupByType(gaps), [gaps]);

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Gaps on {stream}
          </h2>
          <p className="text-xs text-slate-500">
            All gaps logged for this stream — includes gaps raised on individual
            forms.{' '}
            {topBarGeo !== GEO_ALL && (
              <span>Filtered by the selected geography.</span>
            )}
          </p>
        </div>
        {error && (
          <span className="text-sm text-rose-700">Failed to load: {error}</span>
        )}
      </div>

      {loading && <div className="text-sm text-slate-500">Loading gaps…</div>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {GAP_TYPE_ORDER.map((type) => (
          <GapColumn
            key={type}
            stream={stream}
            type={type}
            gaps={grouped.get(type) ?? []}
            onChanged={refresh}
            onOpenDialog={
              type === 'new_form_required' ? () => setDialogOpen(true) : undefined
            }
          />
        ))}
      </div>

      {dialogOpen && (
        <NewFormRequiredDialog
          stream={stream}
          topBarGeo={topBarGeo}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function GapColumn({
  stream,
  type,
  gaps,
  onChanged,
  onOpenDialog,
}: {
  stream: string;
  type: GapType;
  gaps: Gap[];
  onChanged: () => void;
  onOpenDialog?: () => void;
}) {
  const meta = GAP_TYPE_META[type];
  return (
    <div className="card p-3 flex flex-col min-h-[180px]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={'w-2 h-2 rounded-full ' + meta.dotClass} />
          <h3 className="text-sm font-semibold text-slate-900">{meta.label}</h3>
          <span className="text-xs text-slate-500">({gaps.length})</span>
        </div>
        {onOpenDialog && (
          <button
            className="btn-ghost h-7 text-brand-danone"
            onClick={onOpenDialog}
            title={`Flag a new form required for ${stream}`}
          >
            + Add
          </button>
        )}
      </div>

      {gaps.length === 0 && (
        <div className="text-xs text-slate-400 italic">Nothing logged here.</div>
      )}

      <div className="space-y-2">
        {gaps.map((g) => (
          <GapCard key={g.id} gap={g} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function groupByType(gaps: Gap[]): Map<GapType, Gap[]> {
  const m = new Map<GapType, Gap[]>();
  for (const g of gaps) {
    const arr = m.get(g.gap_type) ?? [];
    arr.push(g);
    m.set(g.gap_type, arr);
  }
  return m;
}
